import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Mail, Send, Clock, CheckCheck, Trash2, UserPlus, Check, X, MessageSquare } from "lucide-react";
import { BACKEND_URL } from '@/utils/apiConfig';

const API = `${BACKEND_URL}/api`;

const InviteManagement = ({ invitations, loading, token, onInvitationsUpdate, onFetchInvitations }) => {
  const [inviteEmail, setInviteEmail] = useState("");
  const [sendingInvite, setSendingInvite] = useState(false);
  const [inviteMessage, setInviteMessage] = useState("");
  
  // Invitation Requests state
  const [invitationRequests, setInvitationRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(true);
  const [requestMessage, setRequestMessage] = useState("");
  const [processingRequest, setProcessingRequest] = useState(null);

  // Fetch invitation requests
  const fetchInvitationRequests = async () => {
    try {
      setLoadingRequests(true);
      const response = await axios.get(`${API}/admin/invitation-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInvitationRequests(response.data);
    } catch (err) {
      console.error("Failed to fetch invitation requests:", err);
    } finally {
      setLoadingRequests(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchInvitationRequests();
    }
  }, [token]);

  const handleSendInvite = async (e) => {
    e.preventDefault();
    
    if (!inviteEmail || !inviteEmail.includes('@')) {
      setInviteMessage("❌ Please enter a valid email address");
      setTimeout(() => setInviteMessage(""), 3000);
      return;
    }

    try {
      setSendingInvite(true);
      setInviteMessage("");
      
      await axios.post(`${API}/admin/send-invite`, 
        { email: inviteEmail },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setInviteMessage("✅ Invitation sent successfully!");
      setInviteEmail("");
      
      // Refresh invitations list
      onFetchInvitations();
      
      setTimeout(() => setInviteMessage(""), 5000);
    } catch (err) {
      console.error("Failed to send invitation:", err);
      setInviteMessage(`❌ ${err.response?.data?.detail || "Failed to send invitation"}`);
      setTimeout(() => setInviteMessage(""), 5000);
    } finally {
      setSendingInvite(false);
    }
  };

  const getStatusBadge = (status) => {
    if (status === "used") {
      return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700 flex items-center gap-1">
        <CheckCheck className="w-3 h-3" /> Used
      </span>;
    } else {
      return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-700 flex items-center gap-1">
        <Clock className="w-3 h-3" /> Pending
      </span>;
    }
  };

  const getRequestStatusBadge = (status) => {
    switch (status) {
      case "approved":
        return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700 flex items-center gap-1">
          <Check className="w-3 h-3" /> Approved
        </span>;
      case "rejected":
        return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700 flex items-center gap-1">
          <X className="w-3 h-3" /> Rejected
        </span>;
      default:
        return <span className="px-3 py-1 rounded-full text-xs font-semibold bg-yellow-100 text-yellow-700 flex items-center gap-1">
          <Clock className="w-3 h-3" /> Pending
        </span>;
    }
  };

  const handleDeleteInvitation = async (invitationId) => {
    if (!confirm("Are you sure you want to delete this invitation?")) return;
    
    try {
      await axios.delete(`${API}/admin/invitations/${invitationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Remove from local state
      onInvitationsUpdate(invitations.filter(inv => inv.id !== invitationId));
      setInviteMessage("✅ Invitation deleted successfully");
      setTimeout(() => setInviteMessage(""), 3000);
    } catch (err) {
      console.error("Failed to delete invitation:", err);
      setInviteMessage(`❌ ${err.response?.data?.detail || "Failed to delete invitation"}`);
      setTimeout(() => setInviteMessage(""), 3000);
    }
  };

  const handleResendInvite = async (invitationId) => {
    if (!confirm("Resend invitation email? This will generate a new token.")) return;
    
    try {
      setSendingInvite(true);
      await axios.post(
        `${API}/admin/invitations/${invitationId}/resend`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setInviteMessage("✅ Invitation resent successfully!");
      
      // Refresh invitations list
      onFetchInvitations();
      
      setTimeout(() => setInviteMessage(""), 5000);
    } catch (err) {
      console.error("Failed to resend invitation:", err);
      setInviteMessage(`❌ ${err.response?.data?.detail || "Failed to resend invitation"}`);
      setTimeout(() => setInviteMessage(""), 5000);
    } finally {
      setSendingInvite(false);
    }
  };

  const handleApproveRequest = async (requestId) => {
    try {
      setProcessingRequest(requestId);
      await axios.post(
        `${API}/admin/invitation-requests/${requestId}/approve`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setRequestMessage("✅ Request approved and invitation sent!");
      fetchInvitationRequests();
      onFetchInvitations();
      
      setTimeout(() => setRequestMessage(""), 5000);
    } catch (err) {
      console.error("Failed to approve request:", err);
      setRequestMessage(`❌ ${err.response?.data?.detail || "Failed to approve request"}`);
      setTimeout(() => setRequestMessage(""), 5000);
    } finally {
      setProcessingRequest(null);
    }
  };

  const handleRejectRequest = async (requestId) => {
    if (!confirm("Are you sure you want to reject this request?")) return;
    
    try {
      setProcessingRequest(requestId);
      await axios.post(
        `${API}/admin/invitation-requests/${requestId}/reject`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setRequestMessage("✅ Request rejected");
      fetchInvitationRequests();
      
      setTimeout(() => setRequestMessage(""), 5000);
    } catch (err) {
      console.error("Failed to reject request:", err);
      setRequestMessage(`❌ ${err.response?.data?.detail || "Failed to reject request"}`);
      setTimeout(() => setRequestMessage(""), 5000);
    } finally {
      setProcessingRequest(null);
    }
  };

  const handleDeleteRequest = async (requestId) => {
    if (!confirm("Are you sure you want to delete this request?")) return;
    
    try {
      await axios.delete(`${API}/admin/invitation-requests/${requestId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setInvitationRequests(invitationRequests.filter(req => req.id !== requestId));
      setRequestMessage("✅ Request deleted");
      setTimeout(() => setRequestMessage(""), 3000);
    } catch (err) {
      console.error("Failed to delete request:", err);
      setRequestMessage(`❌ ${err.response?.data?.detail || "Failed to delete request"}`);
      setTimeout(() => setRequestMessage(""), 3000);
    }
  };

  const pendingRequestsCount = invitationRequests.filter(r => r.status === 'pending').length;

  return (
    <div className="space-y-8">
      {/* Invitation Requests Section */}
      <Card className={pendingRequestsCount > 0 ? "border-2 border-cyan-500" : ""}>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="text-2xl flex items-center gap-2">
              <UserPlus className="w-6 h-6 text-cyan-600" />
              Invitation Requests
              {pendingRequestsCount > 0 && (
                <span className="ml-2 px-2 py-1 bg-cyan-500 text-white text-sm rounded-full">
                  {pendingRequestsCount} pending
                </span>
              )}
            </CardTitle>
          </div>
          <p className="text-sm text-gray-600 mt-2">
            Review and approve requests from users who want to join the community.
          </p>
        </CardHeader>
        <CardContent>
          {requestMessage && (
            <div className={`p-3 rounded-lg mb-4 ${
              requestMessage.includes('✅') 
                ? 'bg-green-50 border border-green-200 text-green-700' 
                : 'bg-red-50 border border-red-200 text-red-700'
            }`}>
              {requestMessage}
            </div>
          )}
          
          {loadingRequests ? (
            <p className="text-center py-8 text-gray-600">Loading requests...</p>
          ) : invitationRequests.length === 0 ? (
            <div className="text-center py-12">
              <UserPlus className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600">No invitation requests yet</p>
              <p className="text-sm text-gray-500 mt-2">
                Users can request an invitation from the login page
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {invitationRequests.map((request) => (
                <div
                  key={request.id}
                  className={`p-3 sm:p-4 border rounded-lg ${
                    request.status === 'pending' ? 'bg-cyan-50 border-cyan-200' : 'bg-gray-50'
                  }`}
                >
                  {/* Mobile-optimized layout */}
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      {/* Name (if available) and Status */}
                      <div className="flex items-center gap-2 mb-1">
                        {request.name && (
                          <span className="font-semibold text-gray-900">{request.name}</span>
                        )}
                        {getRequestStatusBadge(request.status)}
                      </div>
                      
                      {/* Email - full width on mobile */}
                      <div className="flex items-center gap-2 mb-2">
                        <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <span className="text-sm sm:text-base text-gray-700 break-all">{request.email}</span>
                      </div>
                      
                      {request.message && (
                        <div className="flex items-start gap-2 mt-2 bg-white/50 p-2 rounded">
                          <MessageSquare className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                          <p className="text-sm text-gray-600 italic">"{request.message}"</p>
                        </div>
                      )}
                      
                      <div className="text-xs text-gray-500 mt-2">
                        Requested {new Date(request.created_at).toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                        {request.reviewed_at && (
                          <> • Reviewed {new Date(request.reviewed_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric'
                          })}</>
                        )}
                      </div>
                    </div>
                    
                    {/* Action buttons - full width on mobile */}
                    <div className="flex items-center gap-2 flex-shrink-0 sm:flex-col sm:items-end">
                      {request.status === 'pending' && (
                        <div className="flex items-center gap-2 w-full sm:w-auto">
                          <Button
                            size="sm"
                            onClick={() => handleApproveRequest(request.id)}
                            disabled={processingRequest === request.id}
                            className="bg-green-500 hover:bg-green-600 text-white flex-1 sm:flex-none"
                            title="Approve and send invitation"
                          >
                            {processingRequest === request.id ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                            ) : (
                              <>
                                <Check className="w-4 h-4 mr-1" />
                                <span className="sm:inline">Approve</span>
                              </>
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleRejectRequest(request.id)}
                            disabled={processingRequest === request.id}
                            className="text-red-600 border-red-300 hover:bg-red-50"
                            title="Reject request"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteRequest(request.id)}
                            className="text-gray-400 hover:text-red-600"
                            title="Delete request"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                      {request.status !== 'pending' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteRequest(request.id)}
                          className="text-gray-400 hover:text-red-600"
                          title="Delete request"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Send Invite Form */}
      <Card>
        <CardHeader>
          <CardTitle className="text-2xl flex items-center gap-2">
            <Send className="w-6 h-6 text-cyan-600" />
            Send Registration Invitation
          </CardTitle>
          <p className="text-sm text-gray-600 mt-2">
            Invite new members to join the George Dives community. Invitations do not expire.
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSendInvite} className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
              <div className="flex-1 min-w-0">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="member@example.com"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                  required
                  data-testid="invite-email-input"
                />
              </div>
              <div className="flex items-end">
                <Button
                  type="submit"
                  disabled={sendingInvite}
                  className="bg-cyan-500 hover:bg-cyan-600 text-white px-8 w-full sm:w-auto"
                  data-testid="send-invite-btn"
                >
                  {sendingInvite ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Invite
                    </>
                  )}
                </Button>
              </div>
            </div>
            {inviteMessage && (
              <div className={`p-3 rounded-lg ${
                inviteMessage.includes('✅') 
                  ? 'bg-green-50 border border-green-200 text-green-700' 
                  : 'bg-red-50 border border-red-200 text-red-700'
              }`}>
                {inviteMessage}
              </div>
            )}
          </form>
        </CardContent>
      </Card>

      {/* Invitations List */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="text-2xl">All Invitations</CardTitle>
            <div className="flex gap-4 text-sm">
              <div className="text-gray-600">
                <span className="font-semibold">{invitations.filter(i => i.status === 'pending').length}</span> Pending
              </div>
              <div className="text-gray-600">
                <span className="font-semibold">{invitations.filter(i => i.status === 'used').length}</span> Used
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-center py-8 text-gray-600">Loading invitations...</p>
          ) : invitations.length === 0 ? (
            <div className="text-center py-12">
              <Mail className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600">No invitations sent yet</p>
              <p className="text-sm text-gray-500 mt-2">Send your first invitation using the form above</p>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Desktop Table Header - Hidden on mobile */}
              <div className="hidden lg:grid grid-cols-12 gap-4 p-4 bg-gray-100 font-semibold text-gray-700 rounded-lg">
                <div className="col-span-5">Email</div>
                <div className="col-span-2">Status</div>
                <div className="col-span-2">Sent</div>
                <div className="col-span-1">Used</div>
                <div className="col-span-2">Actions</div>
              </div>

              {/* Invitation Rows - Responsive */}
              {invitations.map((invitation) => (
                <div
                  key={invitation.id}
                  className="p-4 border rounded-lg hover:bg-gray-50"
                >
                  {/* Mobile Layout */}
                  <div className="lg:hidden space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <span className="font-medium text-gray-900 truncate">{invitation.email}</span>
                      </div>
                      {getStatusBadge(invitation.status)}
                    </div>
                    <div className="flex items-center justify-between text-sm text-gray-600">
                      <span>Sent: {new Date(invitation.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                      <span>
                        {invitation.status === 'used' && invitation.used_at && (
                          <span className="text-green-600 font-medium">
                            Used: {new Date(invitation.used_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </span>
                        )}
                      </span>
                    </div>
                    <div className="flex gap-2 pt-2">
                      {invitation.status === 'pending' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleResendInvite(invitation.id)}
                          className="text-cyan-600 border-cyan-600 hover:bg-cyan-50"
                        >
                          <Send className="w-4 h-4 mr-1" />
                          Resend
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDeleteInvitation(invitation.id)}
                        className="bg-red-500 hover:bg-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* Desktop Layout - Grid */}
                  <div className="hidden lg:grid grid-cols-12 gap-4 items-center">
                    <div className="col-span-5 flex items-center gap-2 min-w-0">
                      <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      <span className="font-medium text-gray-900 break-all">{invitation.email}</span>
                    </div>
                    <div className="col-span-2 flex items-center">
                      {getStatusBadge(invitation.status)}
                    </div>
                    <div className="col-span-2 flex items-center text-sm text-gray-600">
                      {new Date(invitation.created_at).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </div>
                    <div className="col-span-1 flex items-center text-sm text-gray-600">
                      {invitation.status === 'used' && invitation.used_at && (
                        <span className="text-green-600 font-medium">
                          {new Date(invitation.used_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric'
                          })}
                        </span>
                      )}
                    </div>
                    <div className="col-span-2 flex items-center gap-2">
                      {invitation.status === 'pending' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleResendInvite(invitation.id)}
                          className="text-cyan-600 border-cyan-600 hover:bg-cyan-50"
                          title="Resend invitation"
                        >
                          <Send className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDeleteInvitation(invitation.id)}
                        className="bg-red-500 hover:bg-red-600"
                        title="Delete invitation"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default InviteManagement;
